import { gradientCss } from "../native/gradient";
import { inputGradient } from "./native-paint-input";
import { supportsPenSmoothing } from "../native/pen-smoothing";
import { installLineStyleChoices } from "./native-line-style";
import { supportsFill } from "../native/appearance";
import { supportsCorners } from "../native/corners";
import type { SceneNode } from "../scene/model";
import { installNativeIcons } from "./native-icons";

const descriptors = [
  ["pen-smoothing", "Сглаживание", "smoothing", ["pen-smoothing"]],
  ["fill", "Заливка", "fill", ["color"]],
  ["radius", "Скругление углов", "radius", ["radius"]],
  ["opacity", "Непрозрачность", "opacity", ["opacity"]],
  ["stroke-width", "Толщина обводки", "stroke-width", ["stroke-width"]],
  ["line-style", "Стиль обводки", "dashed", ["line-style"]],
  ["stroke-color", "Цвет обводки", "color", ["stroke-color"]],
  ["sides", "Количество углов", "sides", ["sides"]],
  ["star", "Тип «звезда»", "star", ["star-mode"]],
  ["depth", "Глубина лучей", "depth", ["depth"]],
] as const;

/** Contextual controls own only presentation; both inspectors use the same commands. */
export class NativeCompactProperties {
  private root = document.getElementById("native-compact-properties")!;
  private selected = "";
  private popup: string | null = null;
  constructor() {
    new ResizeObserver(() => {
      document
        .getElementById("native-editor")!
        .style.setProperty(
          "--native-compact-height",
          document
            .getElementById("native-context-properties")!
            .getBoundingClientRect().height + "px",
        );
    }).observe(document.getElementById("native-context-properties")!);
    const bank = document.createElement("div");
    bank.hidden = true;
    this.root.append(bank);
    for (const name of ["appearance", "shape"]) {
      const section = document
        .querySelector(`[data-property-section="${name}"]`)!
        .cloneNode(true) as HTMLElement;
      section.removeAttribute("data-property-section");
      for (const el of [
        section,
        ...section.querySelectorAll<HTMLElement>("[id]"),
      ])
        if (el.id) el.id = el.id.replace("native-", "native-compact-");
      bank.append(section);
    }
    const row = document.createElement("div");
    row.className = "native-property-row";
    this.root.append(row);
    for (const [name, title, icon, fields] of descriptors) {
      const anchor = document.createElement("div");
      anchor.className = "native-property-anchor";
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.nativePopover = name;
      button.dataset.nativeIcon = icon;
      button.title = title;
      button.setAttribute("aria-label", title);
      anchor.append(button);
      row.append(anchor);
      if (name === "star") {
        button.onclick = () => {
          this.closePopup();
          (
            document.getElementById(
              "native-compact-star-mode",
            ) as HTMLInputElement
          ).click();
        };
        continue;
      }
      const popup = document.createElement("div");
      popup.id = "native-popup-" + name;
      popup.className = "native-popup native-properties";
      popup.hidden = true;
      popup.setAttribute("role", "region");
      popup.setAttribute("aria-label", title);
      for (const field of fields)
        popup.append(
          document.getElementById("native-compact-" + field)!.parentElement!,
        );
      if (name === "line-style")
        installLineStyleChoices(popup, () => {
          this.closePopup();
        });
      anchor.append(popup);
      button.setAttribute("aria-controls", popup.id);
      button.setAttribute("aria-expanded", "false");
      if (name === "fill" || name === "stroke-color") {
        button.dataset.colorInput = "native-compact-" + fields[0];
        button.onclick = () => this.closePopup();
        continue;
      }
      button.onclick = () => {
        const opening = this.popup !== name;
        this.closePopup();
        if (!opening) return;
        this.popup = name;
        popup.hidden = false;
        button.setAttribute("aria-expanded", "true");
        popup.style.marginLeft = "0px";
        const box = popup.getBoundingClientRect();
        popup.style.marginLeft =
          Math.max(8 - box.left, Math.min(0, innerWidth - 8 - box.right)) +
          "px";
      };
    }
    const alignment = document
      .querySelector('[data-property-section="align"] .native-align')!
      .cloneNode(true) as HTMLElement;
    alignment.id = "native-compact-align";
    alignment.classList.add("native-property-row");
    this.root.append(alignment);
    const duplicate = document.createElement("button");
    duplicate.type = "button";
    duplicate.dataset.nativeDuplicate = "";
    duplicate.dataset.nativeIcon = "copy";
    duplicate.id = "native-compact-duplicate";
    duplicate.title = "Дублировать";
    duplicate.setAttribute("aria-label", "Дублировать");
    row.append(duplicate);
    installNativeIcons(this.root);
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (!this.root.contains(event.target as Node)) this.closePopup();
      },
      true,
    );
    window.addEventListener("resize", () => this.closePopup());
  }
  closePopup() {
    if (!this.popup) return false;
    const popup = document.getElementById("native-popup-" + this.popup)!;
    if (popup.contains(document.activeElement))
      (document.activeElement as HTMLElement).blur();
    popup.hidden = true;
    this.root
      .querySelector(`[data-native-popover="${this.popup}"]`)!
      .setAttribute("aria-expanded", "false");
    this.popup = null;
    return true;
  }
  render(nodes: SceneNode[]) {
    const key = JSON.stringify(nodes.map((n) => n.id));
    if (key !== this.selected) this.closePopup();
    this.selected = key;
    this.root.hidden = !nodes.length;
    for (const [name, , , fields] of descriptors) {
      const button = this.root.querySelector<HTMLButtonElement>(
        `[data-native-popover="${name}"]`,
      )!;
      const source = document.getElementById(
        "native-compact-" + fields[fields.length - 1],
      ) as HTMLInputElement;
      const applicable =
        name === "pen-smoothing"
          ? nodes.some(supportsPenSmoothing)
          : name === "fill"
            ? nodes.some(supportsFill)
            : name === "radius"
              ? nodes.some(supportsCorners)
              : name === "opacity"
                ? !!nodes.length
                : ["sides", "star", "depth"].includes(name)
                  ? nodes.some((n) => n.kind === "polygon") &&
                    (name !== "depth" || !source.disabled)
                  : nodes.some(
                      (n) =>
                        ["rectangle", "ellipse", "polygon"].includes(n.kind) ||
                        !!n.geometry.svgPath,
                    );
      button.parentElement!.hidden = !applicable;
      button.disabled = source.disabled;
      if (name === "line-style") {
        for (const choice of this.root.querySelectorAll<HTMLButtonElement>(
          "[data-native-line-style]",
        )) {
          choice.disabled = source.disabled;
          choice.setAttribute(
            "aria-pressed",
            String(choice.dataset.nativeLineStyle === source.value),
          );
        }
      }
      if (name === "star")
        button.setAttribute(
          "aria-pressed",
          source.indeterminate ? "mixed" : String(source.checked),
        );
      if (name === "fill" || name === "stroke-color") {
        const gradient = inputGradient(source);
        button.style.setProperty(
          "--swatch",
          gradient ? gradientCss(gradient) : source.value,
        );
        button.classList.add("native-color-button");
      }
      if (!applicable && this.popup === name) this.closePopup();
    }
    const radius = this.root.querySelector(
      '[data-native-popover="radius"]',
    )!.parentElement!;
    const opacity = this.root.querySelector(
      '[data-native-popover="opacity"]',
    )!.parentElement!;
    if (nodes.length && nodes.every((n) => n.kind === "image")) {
      if (
        opacity.compareDocumentPosition(radius) &
        Node.DOCUMENT_POSITION_PRECEDING
      )
        opacity.after(radius);
    } else if (
      radius.compareDocumentPosition(opacity) & Node.DOCUMENT_POSITION_PRECEDING
    )
      opacity.before(radius);
    document.getElementById("native-compact-align")!.hidden = nodes.length < 2;
    const duplicate = document.getElementById("native-compact-duplicate")!;
    const destination =
      nodes.length > 1
        ? document.getElementById("native-compact-align")!
        : this.root.querySelector(".native-property-row")!;
    if (duplicate.parentElement !== destination) destination.append(duplicate);
  }
}
